import { useCallback, useEffect, useRef } from 'react';
import { useApp } from '@context/AppContext';
import { useFirebase } from '@/App';
import { useCorpse } from './useCorpse';
import { captureCanvas, captureZone } from '@utils/canvasSnapshot';
import { uploadGameImage, buildSubmissionPath } from '@utils/imageStorage';
import { getZonePreset } from './corpseAssignment';
import GameHUD from '../shared/GameHUD';

/**
 * Active drawing phase for Exquisite Corpse.
 * The current drawer draws on their assigned zone — zone masking is handled
 * automatically by CanvasStage based on game state values.
 *
 * Corpse uses the SHARED canvas (not solo mode) so that strokes sync between
 * players. If a previous game left soloMode on, we explicitly turn it off.
 */
export default function CorpseDrawing({ stageRef }) {
  const { state, dispatch } = useApp();
  const { game, user, room, canvas } = state;
  const firebase = useFirebase();
  const { advanceZone } = useCorpse();
  const initialisedRef = useRef(false);

  const zoneCount = game.zoneCount || 3;
  const preset = getZonePreset(zoneCount);
  const currentZone = game.currentZone ?? 0;
  const currentCreature = game.currentCreature ?? 0;
  const zone = preset[currentZone] || { name: `Zone ${currentZone + 1}`, emoji: '✏️' };
  const isDrawer = game.currentDrawerId === user.id;
  const drawerName = room.members?.[game.currentDrawerId]?.nickname || 'Unknown';

  const creatureLabel = (game.totalCreatures ?? 1) > 1
    ? ` (Creature ${currentCreature + 1}/${game.totalCreatures})`
    : '';

  // Ensure shared canvas mode (corpse is collaborative, not solo).
  useEffect(() => {
    if (initialisedRef.current) return;
    initialisedRef.current = true;
    if (canvas.soloMode) {
      dispatch({ type: 'TOGGLE_SOLO_MODE' });
    }
  }, [dispatch, canvas.soloMode]);

  // On timer expiry: capture snapshot, upload to Storage, then advance
  const handleTimerExpire = useCallback(async () => {
    if (!user.isHost) return;

    if (stageRef) {
      const dataUrl = captureZone(stageRef, currentZone, zoneCount);
      if (dataUrl) {
        const path = buildSubmissionPath(state.room.code,
          `corpse/creature${currentCreature}/zone${currentZone}`, 'zone');
        const imageUrl = await uploadGameImage(dataUrl, path);
        if (imageUrl) {
          await firebase.updateGameState({
            [`creatures/${currentCreature}/zones/${currentZone}/snapshot`]: imageUrl,
          });
        }
      }
    }

    advanceZone();
  }, [user.isHost, stageRef, firebase, currentCreature, currentZone, advanceZone, state.room.code]);

  // Build contextual drawing instruction
  const getInstruction = () => {
    const isFirst = currentZone === 0;
    const isLast = currentZone === zoneCount - 1;

    if (isFirst) {
      return 'Draw from the top of the canvas down to the first dashed line. Leave some lines crossing the boundary so the next artist can connect.';
    }
    if (isLast) {
      return 'Draw from the last dashed line to the bottom. You can see hints from the zone above — connect to them!';
    }
    return 'Draw between the dashed lines for your zone. You can see hints from above — connect to them and leave some lines below for the next artist.';
  };

  return (
    <GameHUD
      timerEndsAt={game.timerEndsAt}
      timerDuration={game.zoneDuration}
      onTimerExpire={handleTimerExpire}
      emoji={zone.emoji}
      label={isDrawer ? `Drawing the ${zone.name}` : `${drawerName} is drawing`}
      sublabel={isDrawer
        ? `Zone ${currentZone + 1} of ${zoneCount}${creatureLabel}`
        : `${zone.name} zone${creatureLabel}`
      }
    >
      <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg border border-slate-100 text-center">
        {isDrawer ? (
          <>
            <p className="text-sm text-charcoal font-display font-medium">
              Draw the <span className="font-bold">{zone.name.toLowerCase()}</span> of a creature — between the dashed lines
            </p>
            <p className="text-xs text-slate-400 font-display mt-1">
              {getInstruction()}
            </p>
          </>
        ) : (
          <p className="text-sm text-slate-500 font-display">
            Waiting for <span className="font-semibold text-charcoal">{drawerName}</span> to draw the{' '}
            <span className="font-semibold">{zone.name.toLowerCase()}</span>...
          </p>
        )}
        <div className="flex justify-center gap-1 mt-2">
          {preset.map((z, i) => (
            <div
              key={z.name}
              className={`w-8 h-2.5 rounded-full transition-colors ${
                i === currentZone
                  ? 'bg-amber animate-pulse'
                  : i < currentZone
                    ? 'bg-emerald-300'
                    : 'bg-slate-200'
              }`}
              title={z.name}
            />
          ))}
        </div>
      </div>
    </GameHUD>
  );
}
